import { ReportingPeriodDto } from "@service/calculation/api/shared/dto/reporting-period.dto";

type FormatStringDateOptions = {
  date: string | Date;
  locale?: string;
  includeTime?: boolean;
  useUserTimeZone?: boolean;
};

export const formatStringDate = ({
  date,
  locale,
  includeTime,
  useUserTimeZone = true,
}: FormatStringDateOptions): string => {
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };

  if (includeTime) {
    options.hour = "numeric";
    options.minute = "numeric";
  }

  if (!useUserTimeZone) {
    options.timeZone = "UTC";
  }

  return new Date(date).toLocaleDateString(locale ?? "en-GB", options);
};

type FormatDottedDateOptions = {
  date: string | Date;
  useUserTimeZone?: boolean;
  year?: "numeric" | "2-digit";
};

export const formatDottedDate = ({
  date,
  useUserTimeZone = true,
  year = "numeric",
}: FormatDottedDateOptions): string => {
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year,
  };

  if (!useUserTimeZone) {
    options.timeZone = "UTC";
  }

  // en-GB with 2-digit day/month/year yields "01/07/2026" — replace "/" with "."
  return new Date(date).toLocaleDateString("en-GB", options).replace(/\//g, ".");
};

export const formatDateOrPlaceholder = (value: string | Date | null | undefined, placeholder = "–"): string => {
  if (value == null || value === "") return placeholder;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? placeholder : formatStringDate({ date });
};

// Supplier invitation surfaces show the due date as "June 30, 2026", not the "30 Jun 2026" the
// grids use. Formatted in UTC because respondBy is date-only: the viewer's zone would shift the
// named day for anyone west of Greenwich.
export const formatLongDateOrPlaceholder = (value: string | Date | null | undefined, placeholder = "–"): string => {
  if (value == null || value === "") return placeholder;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return placeholder;

  return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
};

export const renderReportingPeriod = (reportingPeriod: ReportingPeriodDto, locale?: string): string => {
  return `${formatStringDate({ date: reportingPeriod.startDate, locale, useUserTimeZone: false })} - ${formatStringDate({ date: reportingPeriod.endDate, locale, useUserTimeZone: false })}`;
};

export const getDominantYearOfReportingPeriod = (reportingPeriod: ReportingPeriodDto): number => {
  const start = new Date(reportingPeriod.startDate);
  const end = new Date(reportingPeriod.endDate);

  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  if (endYear - startYear > 1) {
    return startYear;
  }

  const monthsInStartYear = endYear > startYear ? 12 - start.getMonth() : end.getMonth() - start.getMonth() + 1;
  const monthsInEndYear = endYear > startYear ? end.getMonth() + 1 : 0;

  return monthsInStartYear >= monthsInEndYear ? startYear : endYear;
};
